import {
  ActionColumnDetectionConfig,
  ActionDetectionStrategy,
  MobileActionDetectionOptions,
  ExpandableContentConfig,
  ExpandableContentOptions,
  ExpandableDetectionStrategy,
  VisibleColumnConfig,
  VisibleColumnOptions,
  VisibleDetectionStrategy,
} from '../../interfaces/ui/ngx-datatable-mobile.interface';

export const ACTION_COLUMN_PATTERNS: Record<
  string,
  ActionColumnDetectionConfig
> = {
  ACTIONS_EN: {
    namePatterns: ['actions', 'action'],
    confidence: 0.9,
    requiresTemplate: true,
  },
  ACTIONS_ES: {
    namePatterns: ['acciones', 'accion'],
    confidence: 0.9,
    requiresTemplate: true,
  },
  OPERATIONS: {
    namePatterns: ['operations', 'operaciones', 'ops'],
    confidence: 0.8,
    requiresTemplate: true,
  },
  BUTTONS: {
    namePatterns: ['buttons', 'botones', 'btn'],
    confidence: 0.7,
    requiresTemplate: true,
  },
  CONTROLS: {
    namePatterns: ['controls', 'controles', 'ctrl'],
    confidence: 0.6,
    requiresTemplate: true,
  },
} as const;

export const DETECTION_STRATEGIES: Record<ActionDetectionStrategy, number> = {
  'metadata-first': 1,
  'name-pattern': 2,
  'position-based': 3,
  'template-only': 4,
} as const;

export const CRITERIA_WEIGHTS: Record<
  keyof import('../../interfaces/ui/ngx-datatable-mobile.interface').ActionColumnCriteria,
  number
> = {
  isMobileActions: 1.0,
  isFrozenLeft: 0.9,
  hasTemplate: 0.8,
  nameMatches: 0.7,
  isFirstColumn: 0.6,
  hasName: 0.3,
} as const;

export const DEFAULT_DETECTION_OPTIONS: MobileActionDetectionOptions = {
  enableAutoDetection: true,
  namePatterns: Object.values(ACTION_COLUMN_PATTERNS).flatMap(
    (config) => config.namePatterns
  ),
  positionBased: true,
  templateRequired: true,
  confidenceThreshold: 0.5,
} as const;

export const PATTERN_MATCHERS: Record<string, (name: string) => boolean> = {
  exact: (name: string) =>
    Object.values(ACTION_COLUMN_PATTERNS).some((config) =>
      config.namePatterns.includes(name.toLowerCase())
    ),

  includes: (name: string) =>
    Object.values(ACTION_COLUMN_PATTERNS).some((config) =>
      config.namePatterns.some((pattern) =>
        name.toLowerCase().includes(pattern)
      )
    ),

  startsWith: (name: string) =>
    Object.values(ACTION_COLUMN_PATTERNS).some((config) =>
      config.namePatterns.some((pattern) =>
        name.toLowerCase().startsWith(pattern)
      )
    ),

  endsWith: (name: string) =>
    Object.values(ACTION_COLUMN_PATTERNS).some((config) =>
      config.namePatterns.some((pattern) =>
        name.toLowerCase().endsWith(pattern)
      )
    ),
} as const;

export const CONFIDENCE_THRESHOLDS = {
  HIGH: 0.8,
  MEDIUM: 0.6,
  LOW: 0.4,
  MINIMUM: 0.2,
} as const;

export const DETECTION_METHOD_PRIORITY = {
  metadata: 1,
  name: 2,
  position: 3,
  template: 4,
} as const;

export const EXPANDABLE_CONTENT_CONFIGS: Record<
  string,
  ExpandableContentConfig
> = {
  template: {
    type: 'template',
    requiresTemplate: true,
    requiresProperty: false,
    weight: 1.0,
  },
  array: {
    type: 'array',
    requiresTemplate: false,
    requiresProperty: true,
    weight: 0.8,
  },
  object: {
    type: 'object',
    requiresTemplate: false,
    requiresProperty: true,
    weight: 0.7,
  },
} as const;

export const EXPANDABLE_DETECTION_STRATEGIES: Record<
  string,
  ExpandableDetectionStrategy
> = {
  TEMPLATE_FIRST: 'template-first',
  PROPERTY_FIRST: 'property-first',
  DATA_DRIVEN: 'data-driven',
  HYBRID: 'hybrid',
} as const;

export const EXPANDABLE_CRITERIA_WEIGHTS: Record<string, number> = {
  hasExpandableProperty: 0.3,
  hasExpandableCellTemplate: 0.4,
  hasExpandableRowTemplate: 0.5,
  hasArrayData: 0.2,
  hasObjectData: 0.15,
} as const;

export const DEFAULT_EXPANDABLE_OPTIONS: ExpandableContentOptions = {
  enableAutoDetection: true,
  requireTemplate: false,
  requireProperty: false,
  confidenceThreshold: 0.4,
  checkRowData: true,
} as const;

export const EXPANDABLE_CONFIDENCE_THRESHOLDS: Record<string, number> = {
  high: 0.8,
  medium: 0.5,
  low: 0.3,
  minimal: 0.1,
} as const;

export const EXPANDABLE_STRATEGY_PRIORITY: readonly string[] = [
  'template-first',
  'hybrid',
  'property-first',
  'data-driven',
] as const;

export const VISIBLE_COLUMN_CONFIGS: Record<string, VisibleColumnConfig> = {
  DATA: {
    type: 'data',
    patterns: [
      'id',
      'name',
      'title',
      'description',
      'value',
      'amount',
      'date',
      'status',
    ],
    weight: 1.0,
    excludeByDefault: false,
  },
  ACTION: {
    type: 'action',
    patterns: [
      'action',
      'actions',
      'acciones',
      'button',
      'edit',
      'delete',
      'view',
    ],
    weight: 0.8,
    excludeByDefault: true,
  },
  EXPANDABLE: {
    type: 'expandable',
    patterns: ['expand', 'detail', 'details', 'more', 'info'],
    weight: 0.6,
    excludeByDefault: true,
  },
  SYSTEM: {
    type: 'system',
    patterns: ['_id', '__', 'internal', 'meta', 'system'],
    weight: 0.4,
    excludeByDefault: true,
  },
};

export const VISIBLE_DETECTION_STRATEGIES: Record<
  string,
  VisibleDetectionStrategy
> = {
  PROPERTY_FIRST: 'PROPERTY_FIRST',
  PATTERN_BASED: 'PATTERN_BASED',
  HYBRID: 'HYBRID',
  PERMISSIVE: 'PERMISSIVE',
};

export const VISIBLE_CRITERIA_WEIGHTS: Record<string, number> = {
  hasVisibleProperty: 0.4,
  hasValidPropOrName: 0.3,
  isActionColumn: -0.3,
  isExpandableColumn: -0.2,
  matchesExcludePattern: -0.4,
  isEmpty: -0.2,
};

export const DEFAULT_VISIBLE_OPTIONS: VisibleColumnOptions = {
  enableAutoDetection: true,
  respectVisibleProperty: true,
  excludeActionColumns: true,
  excludeExpandableColumns: true,
  excludeEmptyColumns: true,
  customExcludePatterns: [],
  confidenceThreshold: 0.5,
};

export const VISIBLE_CONFIDENCE_THRESHOLDS: Record<string, number> = {
  HIGH: 0.8,
  MEDIUM: 0.5,
  LOW: 0.3,
  MINIMAL: 0.1,
};

export const VISIBLE_STRATEGY_PRIORITY: Record<
  VisibleDetectionStrategy,
  number
> = {
  PROPERTY_FIRST: 1,
  PATTERN_BASED: 2,
  HYBRID: 3,
  PERMISSIVE: 4,
};
